import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useAuth } from "../contexts/AuthContext";
import useSessionPolicy from "../hooks/useSessionPolicy";
import {
  IDLE_TIMEOUT_MS,
  WARNING_MS,
  clearActivity,
  lastActivity,
  markActivity,
  remainingMs,
  setSignOutReason
} from "../services/sessionTimeout";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "wheel", "scroll"];
const WRITE_THROTTLE_MS = 5000;
const TICK_MS = 1000;

/** Signs the user out after `timeoutMs` without activity in any tab, warning `warningMs` before. */
export function SessionTimeoutGuard({ timeoutMs = IDLE_TIMEOUT_MS, warningMs = WARNING_MS }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [secondsLeft, setSecondsLeft] = useState(null);
  const lastWrite = useRef(0);
  const expiring = useRef(false);

  const touch = useCallback(() => {
    const now = Date.now();
    if (now - lastWrite.current < WRITE_THROTTLE_MS) return;
    lastWrite.current = now;
    markActivity(now);
  }, []);

  const expire = useCallback(async () => {
    if (expiring.current) return;
    expiring.current = true;
    setSignOutReason("timeout");
    clearActivity();
    try {
      await logout();
    } finally {
      navigate("/auth/login", { replace: true });
    }
  }, [logout, navigate]);

  useEffect(() => {
    if (!user) return undefined;
    expiring.current = false;
    lastWrite.current = Date.now();
    markActivity(lastWrite.current);
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    const timer = setInterval(() => {
      const left = remainingMs(lastActivity(), Date.now(), timeoutMs);
      if (left <= 0) {
        setSecondsLeft(null);
        expire();
      } else {
        setSecondsLeft(left <= warningMs ? Math.ceil(left / 1000) : null);
      }
    }, TICK_MS);
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, touch));
      clearInterval(timer);
    };
  }, [user, touch, expire, timeoutMs, warningMs]);

  const stay = () => {
    lastWrite.current = Date.now();
    markActivity(lastWrite.current);
    setSecondsLeft(null);
  };

  if (!user || secondsLeft == null) return null;

  return (
    <Modal isOpen centered backdrop="static" data-testid="session-timeout-modal">
      <ModalHeader>{t("session.expiringTitle")}</ModalHeader>
      <ModalBody>{t("session.expiringBody", { seconds: secondsLeft })}</ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={expire}>{t("session.signOutNow")}</Button>
        <Button color="primary" onClick={stay}>{t("session.stay")}</Button>
      </ModalFooter>
    </Modal>
  );
}

/** Uses the idle timeout from the company's session policy. */
export default function PolicySessionTimeoutGuard() {
  const { policy } = useSessionPolicy();
  return <SessionTimeoutGuard timeoutMs={policy.idleMinutes * 60 * 1000} />;
}
